import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from '@/App';
import { ShoppingCart, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getProducts } from '@/data/products';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cfc143ec-270d-4208-9592-8eae45ed356e/artifacts/4ltgv78b_Untitled%20design-2.png';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const { cart } = useContext(CartContext);
  const navigate = useNavigate();

  useEffect(() => {
    // Load products from local data
    const allProducts = getProducts();
    setProducts(allProducts);
  }, []);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, selectedCategory, sortBy]);

  const filterAndSortProducts = () => {
    let filtered = [...products];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    filtered.sort((a, b) => {
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      return a.name.localeCompare(b.name);
    });

    setFilteredProducts(filtered);
  };

  return (
    <div className="app-container" data-testid="products-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" className="logo-container" data-testid="home-link">
            <img src={LOGO_URL} alt="Om Jishnu Logo" className="logo" />
            <span className="brand-name">OM JISHNU</span>
          </Link>
          <div className="nav-links">
            <Link to="/" className="nav-link" data-testid="nav-home">Home</Link>
            <Link to="/products" className="nav-link" data-testid="nav-products">Products</Link>
            <Link to="/admin" className="nav-link" data-testid="nav-admin">Admin</Link>
            <Link to="/checkout" className="cart-badge" data-testid="nav-cart">
              <ShoppingCart size={24} />
              {cart.length > 0 && (
                <span className="cart-count" data-testid="cart-count">{cart.length}</span>
              )}
            </Link>
          </div>
        </div>
      </nav>

      {/* Page Header */}
      <div style={{ background: 'linear-gradient(135deg, #F4C430 0%, #A44A66 100%)', padding: '3rem 2rem', color: 'white' }} data-testid="page-header">
        <div className="container">
          <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '0.5rem' }} data-testid="page-title">Our Products</h1>
          <p style={{ fontSize: '1.2rem', opacity: '0.9' }} data-testid="page-subtitle">Browse our complete collection of printers and supplies</p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: 'white', borderBottom: '1px solid #e5e5e5', padding: '1.5rem 2rem' }} data-testid="filters-section">
        <div className="container" style={{ display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Filter size={20} />
            <span style={{ fontWeight: '600' }}>Filters:</span>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flex: 1 }}>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Laser Printer">Laser Printer</SelectItem>
                <SelectItem value="Inkjet Printer">Inkjet Printer</SelectItem>
                <SelectItem value="Ink Cartridge">Ink Cartridge</SelectItem>
                <SelectItem value="Ink Bottle">Ink Bottle</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[200px]" data-testid="sort-filter">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div style={{ color: '#666' }} data-testid="product-count">
            {filteredProducts.length} products
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="product-grid" data-testid="products-grid">
        {filteredProducts.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '4rem 2rem', color: '#666' }} data-testid="no-products">
            <p style={{ fontSize: '1.2rem' }}>No products found</p>
          </div>
        ) : (
          filteredProducts.map((product) => (
            <div
              key={product.id}
              className="product-card"
              onClick={() => navigate(`/product/${product.id}`)}
              data-testid={`product-card-${product.id}`}
            >
              <img src={product.image_url} alt={product.name} className="product-image" />
              <div className="product-info">
                <div className="product-category" data-testid="product-category">{product.category}</div>
                <h3 className="product-name" data-testid="product-name">{product.name}</h3>
                <p className="product-brand" data-testid="product-brand">{product.brand}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="product-price" data-testid="product-price">₹{product.price.toLocaleString()}</div>
                  <div style={{ color: product.stock > 0 ? '#10b981' : '#ef4444', fontSize: '0.85rem', fontWeight: '600' }} data-testid="product-stock">
                    {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <footer style={{ background: '#1a1a1a', color: 'white', padding: '3rem 2rem', textAlign: 'center', marginTop: '4rem' }} data-testid="footer">
        <div className="container">
          <img src={LOGO_URL} alt="Om Jishnu" style={{ height: '60px', marginBottom: '1rem' }} />
          <p style={{ marginBottom: '0.5rem', fontSize: '1.2rem', fontWeight: '600' }}>OM JISHNU</p>
          <p style={{ color: '#999', marginBottom: '1.5rem' }}>Your trusted partner for printing solutions</p>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>© 2025 Om Jishnu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ProductsPage;